import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  reconnectEdge,
  MiniMap,
  BackgroundVariant,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../../css/flow-builder.css';

import { TriggerNode } from './nodes/TriggerNode';
import { MessageNode } from './nodes/MessageNode';
import { QuestionNode } from './nodes/QuestionNode';
import { ConditionNode } from './nodes/ConditionNode';
import { ActionNode } from './nodes/ActionNode';
import { DelayNode } from './nodes/DelayNode';
import { DateTimeNode } from './nodes/DateTimeNode';
import { ApiNode } from './nodes/ApiNode';
import { WhatsappFlowNode } from './nodes/WhatsappFlowNode';
import { DeletableEdge } from './edges/DeletableEdge';

import { SidebarPalette } from './SidebarPalette';
import { InspectorDrawer } from './InspectorDrawer';
import { SimulatorDrawer } from './SimulatorDrawer';
import { AnalyticsDrawer } from './AnalyticsDrawer';
import { RealMessageTestModal } from './RealMessageTestModal';
import { Save, ArrowLeft, Bot, Check, AlertCircle, Play, Sun, Moon, BarChart3, Download, Upload, HelpCircle, ChevronDown, Send } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { startFlowTour } from './flowTour';
import { collection, query, where, getDocs, limit, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/FirebaseCredential';
import { Container, Box, Group, Divider, Menu, Button, TextInput, Badge, Text } from '@mantine/core';
import BenchBreadcrumbs from '../UIComponent/Components/BenchBreadcrumbs';
import { ROUTES } from '../../routes/routesPaths';

const initialNodes = [
  {
    id: 'trigger_1',
    type: 'triggerNode',
    position: { x: 100, y: 200 },
    data: {
      label: 'Flow Trigger',
      triggerType: 'KEYWORD',
      keywords: [],
    },
  },
];

const initialEdges = [];

export const FlowBuilderApp = ({ flowId, tenantId, backUrl, csrfToken }) => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { fitView } = useReactFlow();
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [flowName, setFlowName] = useState('Lead Qualification Bot');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isRealTestOpen, setIsRealTestOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [contactAttributes, setContactAttributes] = useState([]);
  const [availableWhatsappFlows, setAvailableWhatsappFlows] = useState([]);
  const [liveCollections, setLiveCollections] = useState({
    packages: [],
    classes: [],
    products: [],
    branches: [],
  });
  const fileInputRef = useRef(null);
  const canvasWrapperRef = useRef(null);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const raw = event.dataTransfer.getData('application/reactflow');
      if (!raw) return;

      try {
        const { type, data } = JSON.parse(raw);
        const bounds = canvasWrapperRef.current?.getBoundingClientRect();
        if (!bounds) return;

        const dropX = event.clientX - bounds.left;
        const dropY = event.clientY - bounds.top;

        const id = `node_${Date.now()}`;
        const newNode = {
          id,
          type,
          position: { x: Math.max(50, Math.round(dropX - 140)), y: Math.max(50, Math.round(dropY - 50)) },
          data,
        };
        setNodes((nds) => [...nds, newNode]);
        setSelectedNode(newNode);
      } catch (err) {}
    },
    [nodes]
  );

  const handleExportCanvas = () => {
    const data = {
      version: '1.0',
      name: flowName,
      nodes,
      edges,
      exported_at: new Date().toISOString(),
    };
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${flowName.toLowerCase().replace(/[^a-z0-9]/g, '_')}_flow.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCanvas = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target.result);
        if (json.nodes && Array.isArray(json.nodes)) {
          setNodes(json.nodes);
          setEdges(json.edges || []);
          if (json.name) setFlowName(`${json.name} (Imported)`);
        } else {
          alert('Invalid Flow JSON: "nodes" array missing.');
        }
      } catch (err) {
        alert('Failed to parse Flow JSON file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Register Custom Node Types
  const nodeTypes = useMemo(
    () => ({
      triggerNode: TriggerNode,
      messageNode: MessageNode,
      questionNode: QuestionNode,
      conditionNode: ConditionNode,
      actionNode: ActionNode,
      delayNode: DelayNode,
      dateTimeNode: DateTimeNode,
      apiNode: ApiNode,
      whatsappFlowNode: WhatsappFlowNode,
    }),
    []
  );

  // Custom Edge Types with instant X delete button
  const edgeTypes = useMemo(
    () => ({
      default: DeletableEdge,
      deletable: DeletableEdge,
    }),
    []
  );

  const edgeReconnectSuccessful = useRef(true);

  const onReconnectStart = useCallback(() => {
    edgeReconnectSuccessful.current = false;
  }, []);

  const onReconnect = useCallback((oldEdge, newConnection) => {
    edgeReconnectSuccessful.current = true;
    setEdges((els) => reconnectEdge(oldEdge, { ...newConnection, animated: true, type: 'default' }, els));
  }, []);

  const onReconnectEnd = useCallback((_, edge) => {
    if (!edgeReconnectSuccessful.current) {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }
    edgeReconnectSuccessful.current = true;
  }, []);

  // Right-click any connection line to instantly disconnect it!
  const onEdgeContextMenu = useCallback((event, edge) => {
    event.preventDefault();
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
  }, []);

  // Listen for delete button click on DeletableEdge
  useEffect(() => {
    const handleDeleteEdge = (e) => {
      const edgeId = e.detail?.id;
      if (edgeId) {
        setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
      }
    };
    window.addEventListener('flow:delete-edge', handleDeleteEdge);
    return () => window.removeEventListener('flow:delete-edge', handleDeleteEdge);
  }, []);

  // Load existing flow from Firestore whatsapp_automation_flow
  useEffect(() => {
    if (flowId) {
      const loadFlow = async () => {
        try {
          const docRef = doc(db, 'whatsapp_automation_flow', flowId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data && data.nodes && data.nodes.length > 0) {
              setNodes(data.nodes);
              setEdges(data.edges || []);
              setFlowName(data.name || 'WhatsApp Automation Flow');
              setIsActive(!!data.is_active);

              setTimeout(() => {
                fitView({ padding: 0.2, maxZoom: 0.85, duration: 300 });
              }, 120);
            }
          }
        } catch (err) {
          console.log('Error loading flow from Firestore:', err);
        }
      };
      loadFlow();
    }

    if (tenantId) {
      fetch(`/app/${tenantId}/api/contacts/custom-attributes`)
        .then((res) => res.json())
        .then((attrs) => {
          if (Array.isArray(attrs)) setContactAttributes(attrs);
        })
        .catch(() => {});

      // Fetch real company collections from Firestore (Packages, Classes, Products)
      const fetchRealCollections = async () => {
        try {
          // 1. Fetch real packages
          const pkgQ = query(
            collection(db, 'packages'),
            where('company_id', '==', tenantId),
            limit(25)
          );
          const pkgSnap = await getDocs(pkgQ);
          const pkgs = [];
          pkgSnap.forEach((d) => {
            const data = d.data();
            const priceStr = data.package_total_price != null ? `₹${data.package_total_price}` : '';
            const durationStr = data.standard_package_month ? `${data.standard_package_month} mos` : (data.duration || '');
            const desc = [priceStr, durationStr].filter(Boolean).join(' • ');
            pkgs.push({
              id: d.id,
              title: data.package_name || data.name || 'Package',
              description: desc || (data.discription || data.description || ''),
              branch_id: data.branch_id || 'all',
            });
          });

          // 2. Fetch real classes
          const clsQ = query(
            collection(db, 'companyprofile', tenantId, 'classes'),
            where('class_status', '==', 'active'),
            limit(25)
          );
          const clsSnap = await getDocs(clsQ);
          const clss = [];
          clsSnap.forEach((d) => {
            const data = d.data();
            const bIds = Array.isArray(data.class_branch_id)
              ? data.class_branch_id
              : [data.branch_id || data.class_branch_id].filter(Boolean);
            clss.push({
              id: d.id,
              title: data.class_name || 'Class',
              description: [data.class_time, data.room_name, data.trainer_name].filter(Boolean).join(' • '),
              branch_ids: bIds,
            });
          });

          // 3. Fetch real products
          let prds = [];
          try {
            const prdQ = query(
              collection(db, 'products'),
              where('company_id', '==', tenantId),
              limit(25)
            );
            const prdSnap = await getDocs(prdQ);
            prdSnap.forEach((d) => {
              const data = d.data();
              const priceStr = data.price != null || data.selling_price != null ? `₹${data.selling_price || data.price}` : '';
              prds.push({
                id: d.id,
                title: data.product_name || data.name || 'Product',
                description: [priceStr, data.category].filter(Boolean).join(' • '),
                branch_id: data.branch_id || 'all',
              });
            });
          } catch (e) {}

          // 4. Fetch real company branches
          let brs = [];
          try {
            const brQ = query(
              collection(db, 'branch'),
              where('company_id', '==', tenantId),
              limit(25)
            );
            const brSnap = await getDocs(brQ);
            brSnap.forEach((d) => {
              const data = d.data();
              brs.push({
                id: d.id,
                title: data.name || data.branch_name || 'Branch',
                name: data.name || data.branch_name || 'Branch',
                description: [data.city, data.address, data.phone].filter(Boolean).join(' • '),
                address: data.address || '',
                city: data.city || '',
                phone: data.phone || data.mobile || '',
              });
            });
          } catch (e) {
            console.error('Error fetching branches:', e);
          }

          setLiveCollections({
            packages: pkgs,
            classes: clss,
            products: prds,
            branches: brs,
          });
        } catch (err) {
          console.error('Error fetching live collections from Firestore:', err);
        }
      };

      fetchRealCollections();
    }
  }, [flowId, tenantId]);

  // Auto-launch guided tour once on first visit
  useEffect(() => {
    const tourDone = localStorage.getItem('sms_flow_tour_completed');
    if (!tourDone) {
      const timer = setTimeout(() => {
        startFlowTour(isDark);
        localStorage.setItem('sms_flow_tour_completed', 'true');
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, type: 'default' }, eds)),
    []
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setIsTesting(false);
    setIsAnalyticsOpen(false);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleAddNode = (type, defaultData) => {
    const id = `node_${Date.now()}`;

    const NODE_WIDTH = 340;
    const NODE_HEIGHT = 220;

    const isOverlap = (x, y) => {
      return nodes.some((n) => {
        const nx = n.position?.x ?? 0;
        const ny = n.position?.y ?? 0;
        return Math.abs(x - nx) < NODE_WIDTH && Math.abs(y - ny) < NODE_HEIGHT;
      });
    };

    let posX = 450;
    let posY = 200;

    if (selectedNode) {
      // Place to the right of the currently selected node
      posX = (selectedNode.position?.x ?? 200) + 380;
      posY = selectedNode.position?.y ?? 200;

      while (isOverlap(posX, posY)) {
        posY += 230;
        if (posY > (selectedNode.position?.y ?? 200) + 690) {
          posX += 380;
          posY = selectedNode.position?.y ?? 200;
        }
      }
    } else if (nodes.length > 0) {
      // Find the rightmost node across the entire flow
      const maxX = Math.max(...nodes.map((n) => n.position?.x ?? 0));
      const rightmostNodes = nodes.filter((n) => (n.position?.x ?? 0) >= maxX - 120);
      const avgY = rightmostNodes.reduce((sum, n) => sum + (n.position?.y ?? 0), 0) / rightmostNodes.length;

      posX = maxX + 380;
      posY = Math.round(avgY);

      while (isOverlap(posX, posY)) {
        posY += 230;
        if (posY > 1200) {
          posX += 380;
          posY = 200;
        }
      }
    }

    const newNode = {
      id,
      type,
      position: { x: posX, y: posY },
      data: defaultData,
    };
    setNodes((nds) => [...nds, newNode]);
    // Do not auto-open edit module; user can click the card to edit
  };

  const handleUpdateNode = (id, newData) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          const updated = { ...n, data: newData };
          setSelectedNode(updated);
          return updated;
        }
        return n;
      })
    );
  };

  const handleDeleteNode = useCallback(
    (nodeIdToDelete) => {
      if (!nodeIdToDelete) return;
      if (nodeIdToDelete === 'trigger_1' || nodeIdToDelete.startsWith('trigger_')) {
        alert('The Start Flow Trigger block cannot be removed as it is required to initiate the flow.');
        return;
      }
      setNodes((nds) => nds.filter((n) => n.id !== nodeIdToDelete));
      setEdges((eds) => eds.filter((e) => e.source !== nodeIdToDelete && e.target !== nodeIdToDelete));
      setSelectedNode(null);
    },
    []
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      if (flowId) {
        const docRef = doc(db, 'whatsapp_automation_flow', flowId);
        await updateDoc(docRef, {
          name: flowName,
          is_active: isActive,
          nodes,
          edges,
          updated_at: serverTimestamp(),
        });
      }

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      console.error('Error saving flow to Firestore:', e);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: isDark ? '#121820' : '#f8f9fa',
        color: isDark ? '#f1f5f9' : '#1e293b',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Container
        size="lg"
        px="md"
        py="xs"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {/* BenchAdmin Standard Top Navbar */}
        <Box
          component="header"
          sx={{
            flexShrink: 0,
            paddingTop: '2px',
            paddingBottom: '2px',
          }}
        >
          <Group position="apart" align="center" noWrap>
            {/* Left: BenchBreadcrumbs & Flow Title */}
            <Group spacing="xs" align="center" noWrap>
              <BenchBreadcrumbs
                links={[
                  { label: 'WhatsApp Flows', path: backUrl || ROUTES.FLOWS_LIST || '/flows' },
                  { label: flowName || 'Flow Editor' },
                ]}
              />
              <div id="tour-flow-name" style={{ marginLeft: 6 }}>
                <TextInput
                  size="xs"
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                  placeholder="Rename flow..."
                  title="Click to rename flow"
                  styles={{
                    input: {
                      fontWeight: 600,
                      fontSize: '12px',
                      border: '1px dashed #cbd5e1',
                      backgroundColor: isDark ? '#1e293b' : 'transparent',
                      color: isDark ? '#f1f5f9' : '#334155',
                      height: '28px',
                      width: `${Math.max(flowName.length + 3, 10)}ch`,
                      padding: '2px 8px',
                      borderRadius: '6px',
                      '&:focus': {
                        borderColor: '#dc2742',
                      },
                    },
                  }}
                />
              </div>
            </Group>

            {/* Right: Actions matching BenchAdmin page style */}
            <Group spacing="xs" align="center" noWrap>
              {/* Test Flow Simulator Button */}
              <Button
                id="tour-btn-test"
                size="xs"
                radius="md"
                variant={isTesting ? 'filled' : 'light'}
                color="teal"
                leftIcon={<Play size={14} />}
                onClick={() => {
                  setIsTesting(!isTesting);
                  if (!isTesting) {
                    setSelectedNode(null);
                    setIsAnalyticsOpen(false);
                  }
                }}
                sx={{
                  backgroundColor: isTesting ? '#0ca678 !important' : '#e6fcf5 !important',
                  color: isTesting ? '#ffffff !important' : '#0ca678 !important',
                  border: '1px solid #c3fae8',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: isTesting ? '#099268 !important' : '#c3fae8 !important' },
                }}
              >
                {isTesting ? 'Close Test' : 'Test Flow'}
              </Button>

              {/* Send Real Test WhatsApp Message */}
              <Button
                id="tour-btn-real-test"
                size="xs"
                radius="md"
                variant="filled"
                color="teal"
                leftIcon={<Send size={14} />}
                onClick={() => setIsRealTestOpen(true)}
                title="Send the flow directly to a real WhatsApp mobile number"
                sx={{
                  backgroundColor: '#0ca678 !important',
                  color: '#ffffff !important',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#099268 !important' },
                }}
              >
                Send Real Message
              </Button>

              {/* WhatsApp Live Chat & Inbox */}
              <Button
                size="xs"
                radius="md"
                variant="light"
                color="blue"
                leftIcon={<Bot size={14} />}
                onClick={() => navigate('/whatsapp/chat')}
                title="Open WhatsApp Live Inbox & Customer Chat"
                sx={{
                  backgroundColor: '#e7f5ff !important',
                  color: '#1c7ed6 !important',
                  border: '1px solid #d0ebff',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#d0ebff !important' },
                }}
              >
                Live Chat
              </Button>

              {/* Hidden File Input for Import */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportCanvas}
                accept=".json,application/json"
                style={{ display: 'none' }}
              />

              {/* More Actions Dropdown Menu */}
              <Menu shadow="md" width={200} position="bottom-end" radius="md">
                <Menu.Target>
                  <Button
                    variant="default"
                    size="xs"
                    radius="md"
                    rightIcon={<ChevronDown size={14} />}
                    sx={{
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      borderColor: isDark ? '#334155' : '#ced4da',
                      color: isDark ? '#f1f5f9' : '#495057',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: isDark ? '#2c2e33' : '#f8f9fa' },
                    }}
                  >
                    More Actions
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: isActive ? '#0ca678' : '#94a3b8' }} />}
                    onClick={() => setIsActive(!isActive)}
                  >
                    Status: {isActive ? 'Active (Set Draft)' : 'Draft (Activate)'}
                  </Menu.Item>
                  <Menu.Item
                    icon={<Send size={14} color="#0ca678" />}
                    onClick={() => setIsRealTestOpen(true)}
                  >
                    Send Real WhatsApp Test
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    icon={<HelpCircle size={14} color="#64748b" />}
                    onClick={() => startFlowTour(isDark)}
                  >
                    Guided Tour
                  </Menu.Item>
                  <Menu.Item
                    icon={isDark ? <Sun size={14} color="#f59e0b" /> : <Moon size={14} color="#64748b" />}
                    onClick={toggleTheme}
                  >
                    {isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    icon={<Download size={14} color="#64748b" />}
                    onClick={handleExportCanvas}
                  >
                    Export JSON
                  </Menu.Item>
                  <Menu.Item
                    icon={<Upload size={14} color="#64748b" />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Import JSON
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              {/* Primary BenchAdmin Save Button */}
              <Button
                id="tour-btn-save"
                size="xs"
                radius="md"
                color="red"
                loading={saving}
                leftIcon={saveStatus === 'success' ? <Check size={14} /> : <Save size={14} />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  backgroundColor: '#dc2742 !important',
                  color: '#ffffff !important',
                  fontWeight: 700,
                  '&:hover': { backgroundColor: '#c91f37 !important' },
                }}
              >
                {saveStatus === 'success' ? 'Saved!' : 'Save Flow'}
              </Button>
            </Group>
          </Group>
          <Divider my="xs" />
        </Box>

        {/* Main Builder Canvas Layout Framed Inside Container */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            borderRadius: '12px',
            border: isDark ? '1px solid #1e293b' : '1px solid #e2e8f0',
            backgroundColor: isDark ? '#141b24' : '#ffffff',
            marginBottom: '8px',
          }}
        >
          {/* 100% Full-Width Flow Canvas */}
          <div 
            id="tour-flow-canvas" 
            ref={canvasWrapperRef}
            onDragOver={onDragOver}
            onDrop={onDrop}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
            }}
          >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnect={onReconnect}
            onReconnectStart={onReconnectStart}
            onReconnectEnd={onReconnectEnd}
            onEdgeContextMenu={onEdgeContextMenu}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{ type: 'default', animated: true }}
            reconnectRadius={25}
            edgesReconnectable={true}
            edgesFocusable={true}
            deleteKeyCode={['Backspace', 'Delete']}
            onNodesDelete={(deletedNodes) => {
              const ids = deletedNodes.map((n) => n.id);
              setEdges((eds) => eds.filter((e) => !ids.includes(e.source) && !ids.includes(e.target)));
              setSelectedNode(null);
            }}
            fitView
            fitViewOptions={{
              padding: 0.2,
              maxZoom: 0.85,
            }}
            minZoom={0.1}
            maxZoom={1.5}
            className={isDark ? 'bg-[#121820]' : 'bg-[#f8f9fa]'}
          >
            <Background
              color={isDark ? '#334155' : '#cbd5e1'}
              gap={20}
              size={1.2}
              variant={BackgroundVariant.Dots}
            />
            <Controls className={isDark ? '!bg-slate-900 !border-slate-800 !text-slate-200 !rounded-lg' : '!bg-white !border-slate-200 !text-slate-700 !shadow-sm !rounded-lg'} />
            <MiniMap
              nodeStrokeColor={isDark ? '#475569' : '#94a3b8'}
              nodeColor={isDark ? '#1e293b' : '#e2e8f0'}
              maskColor={isDark ? 'rgba(15, 23, 42, 0.7)' : 'rgba(241, 245, 249, 0.7)'}
              className={isDark ? '!bg-slate-900/90 !border-slate-800 !rounded-xl' : '!bg-white/95 !border-slate-200 !shadow-md !rounded-xl'}
            />
          </ReactFlow>
        </div>

        {/* Left Slide-Out Palette */}
        <SidebarPalette onAddNode={handleAddNode} />

        {/* Right Inspector Drawer */}
        {selectedNode && !isTesting && !isAnalyticsOpen && (
          <InspectorDrawer
            selectedNode={selectedNode}
            nodes={nodes}
            contactAttributes={contactAttributes}
            availableWhatsappFlows={availableWhatsappFlows}
            liveCollections={liveCollections}
            tenantId={tenantId}
            onUpdateNode={handleUpdateNode}
            onDeleteNode={handleDeleteNode}
            onClose={() => setSelectedNode(null)}
          />
        )}

        {/* Right Test Simulator Drawer */}
        {isTesting && (
          <SimulatorDrawer
            flowId={flowId}
            tenantId={tenantId}
            csrfToken={csrfToken}
            nodes={nodes}
            edges={edges}
            liveCollections={liveCollections}
            onClose={() => setIsTesting(false)}
            onOpenRealTest={() => setIsRealTestOpen(true)}
          />
        )}

        {/* Right Analytics Stats Drawer */}
        {isAnalyticsOpen && (
          <AnalyticsDrawer
            flowId={flowId}
            tenantId={tenantId}
            onClose={() => setIsAnalyticsOpen(false)}
          />
        )}

        {/* Real WhatsApp Message Test Modal */}
        {isRealTestOpen && (
          <RealMessageTestModal
            opened={isRealTestOpen}
            onClose={() => setIsRealTestOpen(false)}
            nodes={nodes}
            edges={edges}
            tenantId={tenantId}
            liveCollections={liveCollections}
          />
        )}
        </Box>
      </Container>
    </Box>
  );
};
